/**
 * PayPal Button Component
 *
 * Componente per gestire il pagamento PayPal
 * Integrato con backend Laravel (richiede autenticazione)
 */

import React, { useState } from 'react';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { useSelector } from 'react-redux';
import { createPayPalOrder, capturePayPalOrder } from '../../api/backend';

const PayPalButton = ({ shippingData, onSuccess, onError }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cart = useSelector(state => state.cartAPI);

  const initialOptions = {
    'client-id': process.env.REACT_APP_PAYPAL_CLIENT_ID,
    currency: process.env.REACT_APP_CURRENCY || 'EUR',
    intent: 'capture',
    locale: 'it_IT',
  };

  /**
   * Crea ordine PayPal tramite backend
   * Il token auth è aggiunto automaticamente dall'interceptor axios
   */
  const createOrder = async (data, actions) => {
    try {
      setLoading(true);
      setError(null);

      if (!shippingData || !shippingData.client_email) {
        throw new Error('Dati spedizione mancanti o incompleti');
      }

      if (!cart.items || cart.items.length === 0) {
        throw new Error('Carrello vuoto');
      }

      // Mappa field names → backend API
      const orderData = {
        client_email: shippingData.client_email,
        client_name: shippingData.client_name,
        client_phone: shippingData.client_phone || null,
        shipping_address: shippingData.shipping_address,
        shipping_city: shippingData.shipping_city,
        shipping_zip: shippingData.shipping_zip,
        shipping_country: shippingData.shipping_country,
        order_notes: shippingData.order_notes || null,
      };

      const response = await createPayPalOrder(orderData);
      setLoading(false);

      // Ritorna PayPal order_id per aprire popup
      return response.order_id;

    } catch (err) {
      console.error('Error creating PayPal order:', err);
      setError(err.error || err.message || 'Errore creazione ordine PayPal');
      setLoading(false);
      if (onError) onError(err);
      throw err;
    }
  };

  /**
   * Cattura pagamento PayPal dopo approvazione
   * Il token auth è aggiunto automaticamente dall'interceptor axios
   */
  const onApprove = async (data, actions) => {
    try {
      setLoading(true);
      setError(null);

      const response = await capturePayPalOrder(data.orderID);
      setLoading(false);

      if (onSuccess) onSuccess(response.order);
      return response;

    } catch (err) {
      console.error('Error capturing PayPal payment:', err);
      setError(err.error || err.message || 'Errore cattura pagamento PayPal');
      setLoading(false);
      if (onError) onError(err);
      throw err;
    }
  };

  const onPayPalError = (err) => {
    console.error('PayPal error:', err);
    setError('Errore durante il pagamento PayPal');
    setLoading(false);
    if (onError) onError(err);
  };

  const onCancel = () => {
    setError('Pagamento annullato');
    setLoading(false);
  };

  return (
    <div className="paypal-button-wrapper">
      {error && (
        <div className="paypal-error-alert" role="alert">
          <i className="ti-alert alert-icon"></i>
          <p>{error}</p>
        </div>
      )}

      {loading && (
        <div className="text-center my-3">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Elaborazione pagamento...</span>
          </div>
          <p className="mt-2">Elaborazione pagamento in corso...</p>
        </div>
      )}

      <PayPalScriptProvider options={initialOptions}>
        <PayPalButtons
          style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' }}
          createOrder={createOrder}
          onApprove={onApprove}
          onError={onPayPalError}
          onCancel={onCancel}
          disabled={loading || !cart.items || cart.items.length === 0}
        />
      </PayPalScriptProvider>
    </div>
  );
};

export default PayPalButton;
